"use client";

import { c as _c } from "react/compiler-runtime";
import { LogoutSquare01Icon, Moon02Icon, Sun02Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useQueryClient } from "@tanstack/react-query";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarMenuSkeleton } from "@/components/ui/sidebar";
import { UserAvatar } from "@/components/ui/user-avatar";
import { useClickSound } from "@/hooks/use-click-sound";
import { useFCM } from "@/hooks/use-fcm";
import { authClient } from "@/lib/auth-client";
import { clearClientSessionArtifacts } from "@/lib/session/logout-cleanup";
export function SidebarUserMenu() {
  const $ = _c(74);
  const queryClient = useQueryClient();
  const {
    data: session,
    isPending
  } = authClient.useSession();
  const [isSigningOut, setIsSigningOut] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      preventInit: true
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    cleanupForLogout
  } = useFCM(t0);
  const {
    setTheme,
    resolvedTheme
  } = useTheme();
  const [mounted, setMounted] = useState(false);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      volume: 0.2
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const click = useClickSound(t1);
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setMounted(true);
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== click || $[5] !== mounted || $[6] !== resolvedTheme || $[7] !== setTheme) {
    t4 = () => {
      if (!mounted) {
        return;
      }
      setTheme(resolvedTheme === "light" ? "dark" : "light");
      click();
    };
    $[4] = click;
    $[5] = mounted;
    $[6] = resolvedTheme;
    $[7] = setTheme;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const toggleTheme = t4;
  if (isPending) {
    let t5;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <SidebarMenu><SidebarMenuItem><SidebarMenuSkeleton showIcon={true} /></SidebarMenuItem></SidebarMenu>;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    return t5;
  }
  if (!session?.user) {
    return null;
  }
  const {
    user
  } = session;
  let t5;
  if ($[10] !== cleanupForLogout || $[11] !== isSigningOut || $[12] !== queryClient || $[13] !== user.id) {
    t5 = async function handleSignOut() {
      if (isSigningOut) {
        return;
      }
      setIsSigningOut(true);
      ;
      try {
        ;
        try {
          await cleanupForLogout();
        } catch (t7) {
          const error_0 = t7;
          console.error("Push token cleanup failed during logout", error_0);
        }
        await authClient.signOut({
          fetchOptions: {
            onSuccess: async () => {
              await clearClientSessionArtifacts({
                queryClient,
                userId: user.id
              });
              if (typeof window !== "undefined") {
                window.location.assign("/");
              }
            }
          }
        });
      } catch (t6) {
        const error = t6;
        setIsSigningOut(false);
        toast.error("Failed to log out", {
          description: error instanceof Error ? error.message : "Please try again."
        });
      }
    };
    $[10] = cleanupForLogout;
    $[11] = isSigningOut;
    $[12] = queryClient;
    $[13] = user.id;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const handleSignOut = t5;
  const t6 = user.handle ?? user.id;
  let t7;
  if ($[15] !== t6 || $[16] !== user.image || $[17] !== user.name) {
    t7 = <UserAvatar className="size-9 shrink-0 rounded-full" image={user.image} name={user.name} seed={t6} />;
    $[15] = t6;
    $[16] = user.image;
    $[17] = user.name;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== user.name) {
    t8 = <span className="truncate font-semibold">{user.name}</span>;
    $[19] = user.name;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const t9 = user.phoneNumber || user.email;
  let t10;
  if ($[21] !== t9) {
    t10 = <span className="truncate text-muted-foreground text-xs">{t9}</span>;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t8) {
    t11 = <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">{t8}{t10}</div>;
    $[23] = t10;
    $[24] = t8;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11 || $[27] !== t7) {
    t12 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" className="h-fit data-open:bg-sidebar-accent data-open:text-sidebar-accent-foreground">{t7}{t11}</SidebarMenuButton></DropdownMenuTrigger>;
    $[26] = t11;
    $[27] = t7;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const t13 = user.handle ?? user.id;
  let t14;
  if ($[29] !== t13 || $[30] !== user.image || $[31] !== user.name) {
    t14 = <UserAvatar className="size-9 rounded-full" image={user.image} name={user.name} seed={t13} />;
    $[29] = t13;
    $[30] = user.image;
    $[31] = user.name;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== user.name) {
    t15 = <span className="truncate font-semibold">{user.name}</span>;
    $[33] = user.name;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  const t16 = user.phoneNumber || user.email;
  let t17;
  if ($[35] !== t16) {
    t17 = <span className="truncate text-muted-foreground text-xs">{t16}</span>;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t15 || $[38] !== t17) {
    t18 = <div className="grid flex-1 text-left text-sm leading-tight">{t15}{t17}</div>;
    $[37] = t15;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t14 || $[41] !== t18) {
    t19 = <DropdownMenuLabel className="p-0 font-normal"><div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">{t14}{t18}</div></DropdownMenuLabel>;
    $[40] = t14;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <DropdownMenuSeparator />;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== toggleTheme) {
    t21 = e => {
      e.preventDefault();
      toggleTheme();
    };
    $[44] = toggleTheme;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  const t22 = `absolute transition-all duration-300 ${mounted ? "rotate-0 scale-100 dark:-rotate-90 dark:scale-0" : "opacity-0"}`;
  let t23;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <HugeiconsIcon icon={Sun02Icon} className="size-5" />;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== t22) {
    t24 = <div className={t22}>{t23}</div>;
    $[47] = t22;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  const t25 = `absolute transition-all duration-300 ${mounted ? "rotate-90 scale-0 dark:rotate-0 dark:scale-100" : "opacity-0"}`;
  let t26;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <HugeiconsIcon icon={Moon02Icon} className="size-5" />;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== t25) {
    t27 = <div className={t25}>{t26}</div>;
    $[50] = t25;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] !== t24 || $[53] !== t27) {
    t28 = <div className="relative flex size-5 items-center justify-center">{t24}{t27}</div>;
    $[52] = t24;
    $[53] = t27;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <span>Toggle theme</span>;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] !== t21 || $[57] !== t28) {
    t30 = <DropdownMenuItem className="cursor-pointer border-b-0" onClick={t21}>{t28}{t29}</DropdownMenuItem>;
    $[56] = t21;
    $[57] = t28;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  let t31;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <DropdownMenuSeparator />;
    $[59] = t31;
  } else {
    t31 = $[59];
  }
  let t32;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <HugeiconsIcon icon={LogoutSquare01Icon} className="size-5" />;
    $[60] = t32;
  } else {
    t32 = $[60];
  }
  const t33 = isSigningOut ? "Logging out..." : "Log out";
  let t34;
  if ($[61] !== t33) {
    t34 = <span>{t33}</span>;
    $[61] = t33;
    $[62] = t34;
  } else {
    t34 = $[62];
  }
  let t35;
  if ($[63] !== handleSignOut || $[64] !== isSigningOut || $[65] !== t34) {
    t35 = <DropdownMenuItem className="cursor-pointer border-t-0 text-red-600" disabled={isSigningOut} onClick={handleSignOut}>{t32}{t34}</DropdownMenuItem>;
    $[63] = handleSignOut;
    $[64] = isSigningOut;
    $[65] = t34;
    $[66] = t35;
  } else {
    t35 = $[66];
  }
  let t36;
  if ($[67] !== t19 || $[68] !== t30 || $[69] !== t35) {
    t36 = <DropdownMenuContent className="ml-3.5 w-[--radix-dropdown-menu-trigger-width] min-w-56 font-jakarta" side="bottom" align="end" sideOffset={8}>{t19}{t20}{t30}{t31}{t35}</DropdownMenuContent>;
    $[67] = t19;
    $[68] = t30;
    $[69] = t35;
    $[70] = t36;
  } else {
    t36 = $[70];
  }
  let t37;
  if ($[71] !== t12 || $[72] !== t36) {
    t37 = <SidebarMenu><SidebarMenuItem><DropdownMenu>{t12}{t36}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[71] = t12;
    $[72] = t36;
    $[73] = t37;
  } else {
    t37 = $[73];
  }
  return t37;
}